import { cn } from '@/lib/utils'
import { MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useTranslation } from 'react-i18next'

interface AgendaItem {
    id: string
    time: string
    period: string
    title: string
    description: string
    type: 'court' | 'office' | 'done'
    client?: {
        name: string
        avatar?: string
    }
}

interface AgendaListProps {
    items: AgendaItem[]
    title?: string
    onViewAll?: () => void
    className?: string
}

export function AgendaList({ items, title, onViewAll, className }: AgendaListProps) {
    const { t } = useTranslation()

    const typeStyles = {
        court: {
            timeBg: 'bg-blue-50 dark:bg-blue-900/20',
            timeColor: 'text-primary dark:text-blue-300',
            badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
            badgeText: t('session') || 'جلسة'
        },
        office: {
            timeBg: 'bg-yellow-50 dark:bg-yellow-900/20',
            timeColor: 'text-secondary dark:text-yellow-400',
            badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
            badgeText: t('office_meeting') || 'مكتب'
        },
        done: {
            timeBg: 'bg-muted',
            timeColor: 'text-muted-foreground',
            badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
            badgeText: t('done') || 'تم الإنجاز'
        }
    }

    return (
        <div className={cn(
            "bg-card rounded-xl shadow-sm border border-border overflow-hidden",
            className
        )}>
            {/* Header */}
            {(title || onViewAll) && (
                <div className="p-5 border-b border-border flex items-center justify-between">
                    {title && (
                        <h3 className="font-bold text-foreground flex items-center gap-2">
                            <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {title}
                        </h3>
                    )}
                    {onViewAll && (
                        <button
                            onClick={onViewAll}
                            className="text-xs text-primary dark:text-blue-300 hover:underline"
                        >
                            {t('view_full_schedule')}
                        </button>
                    )}
                </div>
            )}

            {/* Items */}
            <div className="divide-y divide-border">
                {items.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground text-sm">
                        {t('no_appointments_today')}
                    </div>
                ) : (
                    items.map((item) => {
                        const style = typeStyles[item.type]
                        const isDone = item.type === 'done'

                        return (
                            <div
                                key={item.id}
                                className={cn(
                                    "p-4 hover:bg-muted/50 transition-colors flex items-start gap-4",
                                    isDone && "opacity-60"
                                )}
                            >
                                {/* Time Block */}
                                <div className={cn(
                                    "flex flex-col items-center min-w-[60px] rounded-lg p-2 text-center",
                                    style.timeBg
                                )}>
                                    <span className={cn("text-xs font-bold", style.timeColor)}>
                                        {item.time}
                                    </span>
                                    <span className="text-[10px] text-muted-foreground">
                                        {item.period}
                                    </span>
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-start mb-1 gap-2">
                                        <h4 className={cn(
                                            "font-bold text-foreground text-sm",
                                            isDone && "line-through"
                                        )}>
                                            {item.title}
                                        </h4>
                                        <span className={cn(
                                            "text-xs px-2 py-0.5 rounded-full whitespace-nowrap",
                                            style.badge
                                        )}>
                                            {style.badgeText}
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-2 truncate">
                                        {item.description}
                                    </p>
                                    {item.client && (
                                        <div className="flex items-center gap-2">
                                            <Avatar className="w-5 h-5">
                                                <AvatarImage src={item.client.avatar} alt={item.client.name} />
                                                <AvatarFallback className="text-[10px]">
                                                    {item.client.name.charAt(0)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span className="text-xs text-foreground/80">
                                                {item.client.name}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-muted-foreground hover:text-primary shrink-0"
                                >
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </div>
                        )
                    })
                )}
            </div>
        </div>
    )
}
